import { useEffect, useMemo, useState } from 'react'
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Link2,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  X,
  AlertTriangle,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card } from '@/components/ui'
import { cn, formatNumber } from '@/lib/utils'
import {
  createAdsTargetolog,
  fetchAdsAccountMappings,
  fetchAdsAccountMappingSignals,
  fetchAdsTargetologs,
  saveAdsAccountMapping,
  syncAdsAccountMappings,
  renameAdsTargetolog,
  setAdsTargetologActive,
  type AdsAccountMappingRow,
  type AdsAccountMappingSignal,
  type AdsAccountMappingSyncResult,
  type AdsMappingCityOption,
  type AdsMappingStatus,
  type AdsTargetologRow,
} from '@/services/adsAccountMappingService'

type StatusFilter = 'ALL' | 'ASSIGNED' | 'UNASSIGNED' | 'ATTENTION' | AdsMappingStatus

type MappingDraft = {
  targetologId: string
  cityId: string
}

type CityGroup = {
  key: string
  label: string
  rows: AdsAccountMappingRow[]
  unresolvedCount: number
  confirmationCount: number
}

type PartnerGroup = {
  key: string
  label: string
  partnerNumber: number | null
  rows: AdsAccountMappingRow[]
  cityGroups: CityGroup[]
  unresolvedCount: number
  confirmationCount: number
}

const STATUS_META: Record<AdsMappingStatus, { label: string; badge: 'warning' | 'success' | 'neutral' | 'error' }> = {
  UNASSIGNED: { label: 'Не сопоставлено', badge: 'error' },
  PARTIAL: { label: 'Частично', badge: 'warning' },
  NEEDS_CONFIRMATION: { label: 'Нужно подтвердить', badge: 'warning' },
  CONFIRMED: { label: 'Подтверждено', badge: 'success' },
}

const STATUS_FILTERS: Array<{
  value: StatusFilter
  label: string
  countKey: 'all' | 'assigned' | 'unassigned' | 'attention' | 'NEEDS_CONFIRMATION' | 'CONFIRMED'
  className: string
  activeClassName: string
}> = [
  {
    value: 'ALL',
    label: 'Все',
    countKey: 'all',
    className: 'border-slate-200 bg-white text-slate-700',
    activeClassName: 'border-foreground bg-foreground text-background',
  },
  {
    value: 'NEEDS_CONFIRMATION',
    label: 'Нужно подтвердить',
    countKey: 'NEEDS_CONFIRMATION',
    className: 'border-amber-200 bg-amber-50 text-amber-700',
    activeClassName: 'border-amber-500 bg-amber-500 text-white',
  },
  {
    value: 'ATTENTION',
    label: '⚠ Требует внимания',
    countKey: 'attention',
    className: 'border-orange-200 bg-orange-50 text-orange-700',
    activeClassName: 'border-orange-500 bg-orange-500 text-white',
  },
  {
    value: 'UNASSIGNED',
    label: 'Не назначено',
    countKey: 'unassigned',
    className: 'border-rose-200 bg-rose-50 text-rose-700',
    activeClassName: 'border-rose-500 bg-rose-500 text-white',
  },
  {
    value: 'ASSIGNED',
    label: 'Назначено',
    countKey: 'assigned',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    activeClassName: 'border-emerald-500 bg-emerald-500 text-white',
  },
  {
    value: 'CONFIRMED',
    label: 'Подтверждено',
    countKey: 'CONFIRMED',
    className: 'border-sky-200 bg-sky-50 text-sky-700',
    activeClassName: 'border-sky-500 bg-sky-500 text-white',
  },
]

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function getRowTargetologId(row: AdsAccountMappingRow, draft?: MappingDraft): string {
  return draft?.targetologId ?? row.mapped_targetolog_id ?? ''
}

function getRowCityId(row: AdsAccountMappingRow, draft?: MappingDraft): string {
  if (draft) return draft.cityId
  if (row.mapped_city_id !== null) return String(row.mapped_city_id)
  if (row.suggested_city_id !== null) return String(row.suggested_city_id)
  return ''
}

function dedupeCityOptions(row: AdsAccountMappingRow): AdsMappingCityOption[] {
  const seen = new Set<number>()
  const options: AdsMappingCityOption[] = []

  const push = (cityId: number | null, cityName: string | null) => {
    if (cityId === null || !cityName || seen.has(cityId)) return
    seen.add(cityId)
    options.push({ city_id: cityId, city_name: cityName })
  }

  for (const option of row.city_options) push(option.city_id, option.city_name)
  push(row.mapped_city_id, row.mapped_city_name)
  push(row.suggested_city_id, row.suggested_city_name)

  return options.sort((a, b) => a.city_name.localeCompare(b.city_name, 'ru', { sensitivity: 'base' }))
}

function isRowDirty(row: AdsAccountMappingRow, draft?: MappingDraft): boolean {
  if (!draft) return false
  const baseTargetologId = row.mapped_targetolog_id ?? ''
  const baseCityId = row.mapped_city_id !== null
    ? String(row.mapped_city_id)
    : row.suggested_city_id !== null
      ? String(row.suggested_city_id)
      : ''
  return draft.targetologId !== baseTargetologId || draft.cityId !== baseCityId
}

function rowGroupCityLabel(row: AdsAccountMappingRow, draft?: MappingDraft): string {
  const draftCityId = draft?.cityId ? Number(draft.cityId) : null
  if (draftCityId !== null) {
    const option = dedupeCityOptions(row).find(item => item.city_id === draftCityId)
    if (option) return option.city_name
  }
  if (row.mapped_city_name) return row.mapped_city_name
  if (row.suggested_city_name) return row.suggested_city_name
  return 'Город не выбран'
}

function signalLabel(signal: AdsAccountMappingSignal): string {
  switch (signal.signal_code) {
    case 'MATCH_LOST':
      return 'Мэч по имени пропал'
    case 'TARGET_AMBIGUOUS':
      return 'Несколько таргетологов'
    case 'TARGET_CHANGED':
      return signal.suggested_targetolog_name
        ? `В имени: ${signal.suggested_targetolog_name}`
        : 'Найден другой таргетолог'
    case 'CITY_AMBIGUOUS':
      return 'Несколько городов'
    case 'CITY_CHANGED':
      return signal.suggested_city_name
        ? `В имени: ${signal.suggested_city_name}`
        : 'Город изменился'
    default:
      return 'Изменение нужно проверить'
  }
}

function syncSummary(result: AdsAccountMappingSyncResult): string | null {
  const parts: string[] = []
  if (result.inserted_auto > 0) parts.push(`автоматически найдено ${result.inserted_auto}`)
  if (result.conflicts_reopened > 0) parts.push(`на проверку возвращено ${result.conflicts_reopened}`)
  if (result.snapshots_refreshed > 0) parts.push(`безопасных переименований: ${result.snapshots_refreshed}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

function rowIsUnresolved(row: AdsAccountMappingRow): boolean {
  return row.mapping_status === 'UNASSIGNED' || row.mapping_status === 'PARTIAL'
}

export function AdsAccountMappingsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<AdsAccountMappingRow[]>([])
  const [targetologs, setTargetologs] = useState<AdsTargetologRow[]>([])
  const [signalsByAccount, setSignalsByAccount] = useState<Record<number, AdsAccountMappingSignal>>({})
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [savingAccountId, setSavingAccountId] = useState<number | null>(null)
  const [togglingTargetologId, setTogglingTargetologId] = useState<string | null>(null)
  const [creatingTargetolog, setCreatingTargetolog] = useState(false)
  const [editingTargetologId, setEditingTargetologId] = useState<string | null>(null)
  const [editingTargetologName, setEditingTargetologName] = useState('')
  const [renamingTargetologId, setRenamingTargetologId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [showArchivedTargetologs, setShowArchivedTargetologs] = useState(false)
  const [showDirectory, setShowDirectory] = useState(false)
  const [newTargetologName, setNewTargetologName] = useState('')
  const [drafts, setDrafts] = useState<Record<number, MappingDraft>>({})
  const [expandedPartners, setExpandedPartners] = useState<Record<string, boolean>>({})

  const load = async (
    mode: 'initial' | 'refresh' = 'initial',
    runSync = true
  ): Promise<AdsAccountMappingSyncResult | null> => {
    try {
      if (mode === 'initial') setLoading(true)
      else setRefreshing(true)
      setError(null)

      const syncResult = runSync ? await syncAdsAccountMappings() : null

      const [mappingRows, directoryRows, signalRows] = await Promise.all([
        fetchAdsAccountMappings({ includeWithoutSpend: true }),
        fetchAdsTargetologs(true),
        fetchAdsAccountMappingSignals(),
      ])

      setRows(mappingRows)
      setTargetologs(directoryRows)
      setSignalsByAccount(
        signalRows.reduce<Record<number, AdsAccountMappingSignal>>((acc, signal) => {
          acc[signal.account_id] = signal
          return acc
        }, {})
      )

      return syncResult
    } catch (loadError) {
      setError(errorMessage(loadError, 'Не удалось загрузить сопоставления кабинетов'))
      return null
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const activeTargetologs = useMemo(
    () => targetologs
      .filter(row => row.is_active)
      .sort((a, b) => a.display_name.localeCompare(b.display_name, 'ru', { sensitivity: 'base' })),
    [targetologs]
  )

  const targetologOptionsForRow = (currentTargetologId: string): AdsTargetologRow[] => {
    if (!currentTargetologId) return activeTargetologs

    const current = targetologs.find(item => item.id === currentTargetologId)
    if (!current || current.is_active) return activeTargetologs

    // Keep an archived targetologist visible only in the cabinet where they are already assigned.
    return [current, ...activeTargetologs]
  }

  const visibleTargetologs = useMemo(
    () => (showArchivedTargetologs ? targetologs : targetologs.filter(row => row.is_active))
      .sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1
        return a.display_name.localeCompare(b.display_name, 'ru', { sensitivity: 'base' })
      }),
    [showArchivedTargetologs, targetologs]
  )

  const counts = useMemo(() => {
    const initial = {
      all: rows.length,
      assigned: 0,
      unassigned: 0,
      attention: Object.keys(signalsByAccount).length,
      UNASSIGNED: 0,
      PARTIAL: 0,
      NEEDS_CONFIRMATION: 0,
      CONFIRMED: 0,
    }

    return rows.reduce((acc, row) => {
      acc[row.mapping_status] += 1
      if (row.mapped_targetolog_id && row.mapped_city_id !== null) acc.assigned += 1
      else acc.unassigned += 1
      return acc
    }, initial)
  }, [rows, signalsByAccount])

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase().replace(/ё/g, 'е')

    return rows.filter((row) => {
      if (statusFilter === 'ATTENTION') {
        if (!signalsByAccount[row.account_id]) return false
      } else if (statusFilter === 'ASSIGNED') {
        if (!(row.mapped_targetolog_id && row.mapped_city_id !== null)) return false
      } else if (statusFilter === 'UNASSIGNED') {
        if (row.mapped_targetolog_id && row.mapped_city_id !== null) return false
      } else if (statusFilter !== 'ALL' && row.mapping_status !== statusFilter) {
        return false
      }

      if (!normalizedSearch) return true

      const haystack = [
        row.account_name,
        row.partner_name,
        row.channel,
        row.mapped_targetolog_name,
        row.mapped_city_name,
        row.suggested_city_name,
        signalsByAccount[row.account_id]?.signal_message,
        signalsByAccount[row.account_id]?.suggested_targetolog_name,
        signalsByAccount[row.account_id]?.suggested_city_name,
        row.partner_number === null ? null : String(row.partner_number),
        String(row.account_id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .replace(/ё/g, 'е')

      return haystack.includes(normalizedSearch)
    })
  }, [rows, search, statusFilter, signalsByAccount])

  const groupedRows = useMemo<PartnerGroup[]>(() => {
    const partnerMap = new Map<string, AdsAccountMappingRow[]>()

    for (const row of filteredRows) {
      const key = row.partner_number !== null ? `partner:${row.partner_number}` : `account:${row.account_id}`
      if (!partnerMap.has(key)) partnerMap.set(key, [])
      partnerMap.get(key)!.push(row)
    }

    return Array.from(partnerMap.entries())
      .map(([key, partnerRows]) => {
        const first = partnerRows[0]
        const cityMap = new Map<string, AdsAccountMappingRow[]>()

        for (const row of partnerRows) {
          const label = rowGroupCityLabel(row, drafts[row.account_id])
          if (!cityMap.has(label)) cityMap.set(label, [])
          cityMap.get(label)!.push(row)
        }

        const cityGroups = Array.from(cityMap.entries())
          .map(([label, cityRows]) => ({
            key: `${key}:${label}`,
            label,
            rows: cityRows.sort((a, b) => {
              const aNeeds = a.mapping_status === 'NEEDS_CONFIRMATION' ? 0 : rowIsUnresolved(a) ? 1 : 2
              const bNeeds = b.mapping_status === 'NEEDS_CONFIRMATION' ? 0 : rowIsUnresolved(b) ? 1 : 2
              if (aNeeds !== bNeeds) return aNeeds - bNeeds
              return (a.account_name || '').localeCompare(b.account_name || '', 'ru', { sensitivity: 'base' })
            }),
            unresolvedCount: cityRows.filter(rowIsUnresolved).length,
            confirmationCount: cityRows.filter(row => row.mapping_status === 'NEEDS_CONFIRMATION').length,
          }))
          .sort((a, b) => {
            const aWeight = a.label === 'Город не выбран' ? -1 : 0
            const bWeight = b.label === 'Город не выбран' ? -1 : 0
            if (aWeight !== bWeight) return aWeight - bWeight
            return a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' })
          })

        return {
          key,
          label: first.partner_name || `Кабинет ${first.account_id}`,
          partnerNumber: first.partner_number,
          rows: partnerRows,
          cityGroups,
          unresolvedCount: partnerRows.filter(rowIsUnresolved).length,
          confirmationCount: partnerRows.filter(row => row.mapping_status === 'NEEDS_CONFIRMATION').length,
        }
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' })
      })
  }, [drafts, filteredRows])

  useEffect(() => {
    setExpandedPartners((current) => {
      const next = { ...current }
      let changed = false
      for (const group of groupedRows) {
        if (next[group.key] === undefined) {
          next[group.key] = false
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [groupedRows])

  const updateDraft = (accountId: number, patch: Partial<MappingDraft>) => {
    setDrafts((current) => {
      const previous = current[accountId]
      const row = rows.find(item => item.account_id === accountId)
      const next: MappingDraft = {
        targetologId: patch.targetologId ?? previous?.targetologId ?? row?.mapped_targetolog_id ?? '',
        cityId: patch.cityId ?? previous?.cityId ?? (
          row?.mapped_city_id !== null && row?.mapped_city_id !== undefined
            ? String(row.mapped_city_id)
            : row?.suggested_city_id !== null && row?.suggested_city_id !== undefined
              ? String(row.suggested_city_id)
              : ''
        ),
      }
      return { ...current, [accountId]: next }
    })
  }

  const handleSave = async (row: AdsAccountMappingRow, confirm = true) => {
    const draft = drafts[row.account_id]
    const targetologId = getRowTargetologId(row, draft) || null
    const cityId = getRowCityId(row, draft)
    const parsedCityId = cityId ? Number(cityId) : null

    if (!targetologId && parsedCityId === null) {
      setError(`Для кабинета ${row.account_id} нужно выбрать хотя бы город или таргетолога`)
      return
    }

    try {
      setSavingAccountId(row.account_id)
      setError(null)
      setSuccess(null)

      await saveAdsAccountMapping({
        accountId: row.account_id,
        targetologId,
        cityId: parsedCityId,
        isConfirmed: confirm,
        source: 'manual',
      })

      setDrafts((current) => {
        const next = { ...current }
        delete next[row.account_id]
        return next
      })
      setSuccess(`Кабинет ${row.account_id} сохранён`)
      await load('refresh', false)
    } catch (saveError) {
      setError(errorMessage(saveError, `Не удалось сохранить кабинет ${row.account_id}`))
    } finally {
      setSavingAccountId(null)
    }
  }

  const handleCreateTargetolog = async () => {
    const trimmed = newTargetologName.trim()
    if (!trimmed) return

    try {
      setCreatingTargetolog(true)
      setError(null)
      setSuccess(null)
      await createAdsTargetolog(trimmed)
      setNewTargetologName('')

      const syncResult = await syncAdsAccountMappings()
      await load('refresh', false)

      const summary = syncSummary(syncResult)
      setSuccess(
        summary
          ? `Таргетолог «${trimmed}» добавлен · ${summary}`
          : `Таргетолог «${trimmed}» добавлен. Новых автоматических совпадений нет.`
      )
    } catch (createError) {
      setError(errorMessage(createError, 'Не удалось добавить таргетолога'))
    } finally {
      setCreatingTargetolog(false)
    }
  }

  const startEditingTargetolog = (row: AdsTargetologRow) => {
    setEditingTargetologId(row.id)
    setEditingTargetologName(row.display_name)
    setError(null)
    setSuccess(null)
  }

  const cancelEditingTargetolog = () => {
    setEditingTargetologId(null)
    setEditingTargetologName('')
  }

  const handleRenameTargetolog = async (row: AdsTargetologRow) => {
    const trimmed = editingTargetologName.trim()
    if (!trimmed || trimmed === row.display_name) {
      cancelEditingTargetolog()
      return
    }

    try {
      setRenamingTargetologId(row.id)
      setError(null)
      setSuccess(null)
      await renameAdsTargetolog(row.id, trimmed)
      cancelEditingTargetolog()

      const syncResult = await syncAdsAccountMappings()
      await load('refresh', false)

      const summary = syncSummary(syncResult)
      setSuccess(
        summary
          ? `«${row.display_name}» переименован в «${trimmed}» · ${summary}`
          : `«${row.display_name}» переименован в «${trimmed}»`
      )
    } catch (renameError) {
      setError(errorMessage(renameError, 'Не удалось переименовать таргетолога'))
    } finally {
      setRenamingTargetologId(null)
    }
  }

  const handleToggleTargetolog = async (row: AdsTargetologRow) => {
    if (row.is_active) {
      const confirmed = window.confirm(
        `Переместить «${row.display_name}» в архив?\n\nСуществующие связи с кабинетами сохранятся. Для новых назначений таргетолог будет недоступен.`
      )
      if (!confirmed) return
    }

    try {
      setTogglingTargetologId(row.id)
      setError(null)
      setSuccess(null)
      await setAdsTargetologActive(row.id, !row.is_active)

      if (row.is_active) {
        await load('refresh', false)
        setSuccess(`Таргетолог «${row.display_name}» перемещён в архив. Существующие связи сохранены.`)
      } else {
        const syncResult = await syncAdsAccountMappings()
        await load('refresh', false)
        const summary = syncSummary(syncResult)
        setSuccess(
          summary
            ? `Таргетолог «${row.display_name}» восстановлен · ${summary}`
            : `Таргетолог «${row.display_name}» восстановлен`
        )
      }
    } catch (toggleError) {
      setError(errorMessage(toggleError, 'Не удалось обновить статус таргетолога'))
    } finally {
      setTogglingTargetologId(null)
    }
  }

  const handleRefresh = async () => {
    setSuccess(null)
    const syncResult = await load('refresh', true)
    if (!syncResult) return
    const summary = syncSummary(syncResult)
    setSuccess(summary ? `Проверка завершена · ${summary}` : 'Проверка завершена. Изменений нет.')
  }

  const togglePartner = (key: string) => {
    setExpandedPartners((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f5f3ee] px-3 py-4 sm:px-4 lg:px-6">
      <div className="w-full space-y-4">
        <Card className="border border-[#e7ded0] bg-[#fffdfa] shadow-sm">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={() => navigate('/management-dashboard')}
                  className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3 text-sm font-medium leading-none text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Назад на дашборд УК
                </button>

                <div>
                  <h1 className="text-[28px] font-semibold leading-none text-slate-950">Сопоставление кабинетов</h1>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDirectory(value => !value)}
                  className="inline-flex h-10 min-h-10 w-full shrink-0 flex-nowrap items-center justify-center gap-2 whitespace-nowrap rounded-xl border-slate-300 px-4 text-center leading-none text-slate-700 hover:bg-slate-100 hover:text-slate-900 sm:w-auto"
                >
                  <Settings2 className="h-4 w-4 shrink-0" />
                  {showDirectory ? 'Скрыть справочник' : 'Справочник таргетологов'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleRefresh()}
                  disabled={refreshing}
                  className="inline-flex h-10 min-h-10 w-full shrink-0 flex-nowrap items-center justify-center gap-2 whitespace-nowrap rounded-xl border-slate-300 px-4 text-center leading-none text-slate-700 hover:bg-slate-100 hover:text-slate-900 sm:w-auto"
                >
                  {refreshing ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <RefreshCw className="h-4 w-4 shrink-0" />}
                  Обновить
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((filter) => {
                const active = statusFilter === filter.value
                return (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setStatusFilter(filter.value)}
                    className={cn(
                      'inline-flex h-10 items-center justify-center whitespace-nowrap rounded-full border px-4 text-sm font-medium leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300',
                      active ? filter.activeClassName : filter.className
                    )}
                  >
                    {filter.label}: {formatNumber(counts[filter.countKey])}
                  </button>
                )
              })}
            </div>
          </div>
        </Card>

        {showDirectory ? (
          <Card className="border border-slate-200 bg-white shadow-sm">
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-slate-950">Справочник таргетологов</h2>
                <Badge variant="neutral">{activeTargetologs.length} активных</Badge>
              </div>

              <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    value={newTargetologName}
                    onChange={(event) => setNewTargetologName(event.target.value)}
                    placeholder="Добавить новое имя"
                    className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
                  />
                  <Button
                    size="sm"
                    onClick={() => void handleCreateTargetolog()}
                    disabled={creatingTargetolog}
                    className="inline-flex h-10 min-h-10 w-full shrink-0 flex-nowrap items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-center leading-none sm:w-auto"
                  >
                    {creatingTargetolog ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Plus className="h-4 w-4 shrink-0" />}
                    Добавить
                  </Button>
                </div>
                <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 lg:ml-auto lg:shrink-0">
                  <input
                    type="checkbox"
                    checked={showArchivedTargetologs}
                    onChange={(event) => setShowArchivedTargetologs(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <Archive className="h-4 w-4 shrink-0" />
                  <span>Показывать архив</span>
                </label>
              </div>

              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5">
                {visibleTargetologs.map((row) => {
                  const toggling = togglingTargetologId === row.id
                  const renaming = renamingTargetologId === row.id
                  const isEditing = editingTargetologId === row.id
                  const busy = toggling || renaming

                  return (
                    <div
                      key={row.id}
                      className={cn(
                        'min-h-[60px] rounded-lg border px-3 py-2',
                        row.is_active ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-slate-100/70'
                      )}
                    >
                      {isEditing ? (
                        <div className="flex min-h-[40px] items-center gap-1.5">
                          <input
                            autoFocus
                            value={editingTargetologName}
                            onChange={(event) => setEditingTargetologName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') void handleRenameTargetolog(row)
                              if (event.key === 'Escape') cancelEditingTargetolog()
                            }}
                            disabled={renaming}
                            className="h-8 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                            aria-label={`Новое имя для ${row.display_name}`}
                          />
                          <button
                            type="button"
                            onClick={() => void handleRenameTargetolog(row)}
                            disabled={renaming || !editingTargetologName.trim()}
                            title="Сохранить имя"
                            aria-label="Сохранить имя"
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-fixed-emerald-600 text-white transition-colors hover:bg-fixed-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {renaming ? (
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-white" strokeWidth={2.25} />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 shrink-0 text-white" strokeWidth={2.25} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditingTargetolog}
                            disabled={renaming}
                            title="Отменить редактирование"
                            aria-label="Отменить редактирование"
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X className="h-4 w-4 shrink-0 text-slate-700" strokeWidth={2.25} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex min-h-[40px] items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-[15px] font-medium leading-5 text-slate-900">{row.display_name}</div>
                            <div className="mt-0.5 text-[11px] uppercase tracking-[0.08em] text-slate-500">
                              {row.is_active ? 'Активен' : 'В архиве'}
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              onClick={() => startEditingTargetolog(row)}
                              disabled={busy}
                              title="Редактировать имя"
                              aria-label={`Редактировать имя ${row.display_name}`}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Pencil className="h-4 w-4 shrink-0 stroke-current [stroke-width:2.25]" aria-hidden="true" />
                            </button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleToggleTargetolog(row)}
                              disabled={busy}
                              title={row.is_active ? 'Переместить в архив' : 'Восстановить из архива'}
                              className={cn(
                                'inline-flex h-8 min-h-8 shrink-0 items-center justify-center gap-1 rounded-lg px-2.5 text-center text-[11px] font-medium leading-none',
                                row.is_active
                                  ? 'border-amber-200 bg-amber-50/50 text-amber-700 hover:bg-amber-100 hover:text-amber-800'
                                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                              )}
                            >
                              {toggling ? (
                                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                              ) : row.is_active ? (
                                <>
                                  <Archive className="h-3.5 w-3.5 shrink-0" />
                                  <span>Архив</span>
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                                  <span>Восстановить</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </Card>
        ) : null}

        {error ? (
          <Card className="border border-rose-200 bg-rose-50 text-rose-700 shadow-sm">
            <div className="flex items-start gap-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{error}</div>
            </div>
          </Card>
        ) : null}

        {success ? (
          <Card className="border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm">
            <div className="flex items-start gap-3 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{success}</div>
            </div>
          </Card>
        ) : null}

        <Card className="border border-slate-200 bg-white shadow-sm">
          <div>
            <label className="block w-full">
              <div className="sr-only">Поиск</div>
              <div className="flex h-10 w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="кабинет, франчайзи, город, таргетолог"
                  className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>
            </label>
          </div>
        </Card>

        <div className="space-y-3">
          {groupedRows.map((group) => {
            const isOpen = expandedPartners[group.key] ?? false
            return (
              <Card key={group.key} className="border border-slate-200 bg-white p-0 shadow-sm">
                <button
                  type="button"
                  onClick={() => togglePartner(group.key)}
                  className="flex min-h-[64px] w-full items-center justify-between gap-4 rounded-[inherit] px-4 py-3 text-left transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-300"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-semibold text-slate-950">{group.label}</div>
                      {group.partnerNumber !== null ? (
                        <span className="text-sm text-slate-500">№{group.partnerNumber}</span>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      <Badge variant="neutral" className="bg-slate-100 text-slate-700">
                        {group.rows.length} кабинетов
                      </Badge>
                      {group.confirmationCount > 0 ? (
                        <Badge variant="warning" className="bg-amber-50 text-amber-700">
                          {group.confirmationCount} на подтверждение
                        </Badge>
                      ) : null}
                      {group.unresolvedCount > 0 ? (
                        <Badge variant="error" className="bg-rose-50 text-rose-700">
                          {group.unresolvedCount} не заполнено
                        </Badge>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {group.cityGroups.length > 1 ? (
                      <div className="text-right text-xs text-slate-500">
                        {group.cityGroups.length} {group.cityGroups.length === 1 ? 'группа' : group.cityGroups.length < 5 ? 'группы' : 'групп'}
                      </div>
                    ) : null}
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                  </div>
                </button>

                {isOpen ? (
                  <div className="border-t border-slate-200 px-4 py-3">
                    <div className="space-y-4">
                      {group.cityGroups.map((cityGroup) => (
                        <div key={cityGroup.key} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                          <div className="flex flex-col gap-2 border-b border-slate-200 pb-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-sm font-semibold text-slate-950">{cityGroup.label}</div>
                              <Badge variant="neutral" className="bg-white text-slate-700">
                                {cityGroup.rows.length}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {cityGroup.confirmationCount > 0 ? (
                                <Badge variant="warning" className="bg-amber-50 text-amber-700">
                                  {cityGroup.confirmationCount} подтвердить
                                </Badge>
                              ) : null}
                              {cityGroup.unresolvedCount > 0 ? (
                                <Badge variant="error" className="bg-rose-50 text-rose-700">
                                  {cityGroup.unresolvedCount} не заполнено
                                </Badge>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-2 overflow-x-auto">
                            <table className="min-w-[980px] w-full border-collapse text-sm">
                              <thead className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                <tr>
                                  <th className="px-2 py-2 text-left font-medium">Кабинет</th>
                                  <th className="px-2 py-2 text-left font-medium">Канал</th>
                                  <th className="px-2 py-2 text-left font-medium">Город</th>
                                  <th className="px-2 py-2 text-left font-medium">Таргетолог</th>
                                  <th className="px-2 py-2 text-left font-medium">Статус</th>
                                  <th className="px-2 py-2 text-right font-medium">Действие</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {cityGroup.rows.map((row) => {
                              const draft = drafts[row.account_id]
                              const cityOptions = dedupeCityOptions(row)
                              const currentTargetologId = getRowTargetologId(row, draft)
                              const currentCityId = getRowCityId(row, draft)
                              const dirty = isRowDirty(row, draft)
                              const saving = savingAccountId === row.account_id
                              const statusMeta = STATUS_META[row.mapping_status]
                              const signal = signalsByAccount[row.account_id]

                              return (
                                <tr key={row.account_id} className="bg-white">
                                  <td className="px-2 py-2 align-middle">
                                    <div className="font-medium text-slate-950">{row.account_name || `Кабинет ${row.account_id}`}</div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                      <span>ID {row.account_id}</span>
                                      {signal ? (
                                        <span
                                          title={`${signal.signal_message}${signal.previous_account_name ? `\nБыло: ${signal.previous_account_name}` : ''}${signal.current_account_name ? `\nСейчас: ${signal.current_account_name}` : ''}`}
                                          className={cn(
                                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium',
                                            signal.signal_level === 'error'
                                              ? 'border-rose-200 bg-rose-50 text-rose-700'
                                              : 'border-amber-200 bg-amber-50 text-amber-700'
                                          )}
                                        >
                                          <AlertTriangle className="h-3 w-3 shrink-0" />
                                          {signalLabel(signal)}
                                        </span>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 align-middle text-sm text-slate-600">
                                    {row.channel}
                                  </td>
                                  <td className="px-2 py-2 align-middle">
                                      <select
                                        value={currentCityId}
                                        onChange={(event) => updateDraft(row.account_id, { cityId: event.target.value })}
                                        className="h-9 w-full min-w-[180px] rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-900 outline-none focus:border-slate-300"
                                      >
                                        <option value="">Не выбран</option>
                                        {cityOptions.map((option) => (
                                          <option key={option.city_id} value={option.city_id}>{option.city_name}</option>
                                        ))}
                                      </select>
                                  </td>
                                  <td className="px-2 py-2 align-middle">
                                      <select
                                        value={currentTargetologId}
                                        onChange={(event) => updateDraft(row.account_id, { targetologId: event.target.value })}
                                        className="h-9 w-full min-w-[190px] rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-900 outline-none focus:border-slate-300"
                                      >
                                        <option value="">Не выбран</option>
                                        {targetologOptionsForRow(currentTargetologId).map((targetolog) => (
                                          <option
                                            key={targetolog.id}
                                            value={targetolog.id}
                                            disabled={!targetolog.is_active}
                                          >
                                            {targetolog.display_name}{targetolog.is_active ? '' : ' (архив)'}
                                          </option>
                                        ))}
                                      </select>
                                  </td>
                                  <td className="px-2 py-2 align-middle">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge variant={statusMeta.badge}>{statusMeta.label}</Badge>
                                      <span className="text-xs text-slate-500">{row.source === 'auto' ? 'Авто' : 'Ручной'}</span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 align-middle text-right">
                                      <Button
                                        size="sm"
                                        onClick={() => void handleSave(row, true)}
                                        disabled={saving}
                                        className={cn('inline-flex h-9 min-h-9 min-w-[132px] shrink-0 flex-nowrap items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 text-center leading-none', !dirty && row.mapping_status === 'CONFIRMED' ? 'opacity-80' : '')}
                                      >
                                      {saving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
                                        {row.mapping_status === 'NEEDS_CONFIRMATION' ? 'Подтвердить' : 'Сохранить'}
                                      </Button>
                                  </td>
                                </tr>
                              )
                            })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Card>
            )
          })}

          {groupedRows.length === 0 ? (
            <Card className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500 shadow-sm">
              По текущим фильтрам ничего не найдено.
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  )
}